'use client';

import Image from '@components/common/ImageFallback';
import { useTranslation } from '@root/src/i18n/client';
import Link from 'next/link';

interface CollectionCardProps {
  slug: string;
  name: string;
  description?: string;
  image?: string | string[];
}

export default function CollectionCard({
  slug,
  name,
  description,
  image,
}: CollectionCardProps) {
  const { t } = useTranslation('common');
  const imageUrl = Array.isArray(image) ? image[0] : image;

  return (
    <Link href={`/collection/${slug}`} scroll className="group block h-full">
      <article className="flex h-full min-h-[270px] flex-col justify-between rounded-[24px] border border-[#e8dec8] bg-[#faf7ef] p-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:min-h-[348px] sm:rounded-[28px] sm:p-4">
        <div className="overflow-hidden rounded-[22px]">
          <Image
            src={imageUrl || '/images/placeholder-tea.jpg'}
            alt={name}
            width={400}
            height={280}
            sizes="(max-width: 639px) calc(50vw - 24px), (max-width: 1023px) calc(50vw - 48px), (max-width: 1279px) calc(20vw - 28px), 260px"
            className="h-[150px] w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-[220px]"
          />
        </div>

        <div className="mt-3 flex flex-1 flex-col justify-between text-center sm:mt-5">
          <div>
            <h3
              className="text-[18px] font-semibold text-[#3b3b32] md:text-[22px] lg:text-[24px]"
              style={{
                fontFamily: 'Georgia, Times New Roman, serif',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: '1.1em',
                maxHeight: '2.2em',
              }}
            >
              {name}
            </h3>

            <p
              className="mt-2 text-[12px] leading-5 text-[#8c8678] sm:mt-3 sm:text-[13px]"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                minHeight: '2.6rem',
              }}
            >
              {description || t('lytra.collectionCardFallbackDesc')}
            </p>
          </div>

          <div className="mt-3 sm:mt-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#d7ccb0] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.13em] text-[#585647] transition-all duration-300 group-hover:bg-[#efe8d6] sm:gap-3 sm:px-6 sm:text-[13px] sm:tracking-[0.15em]">
              {t('lytra.discover')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
